import React, { useState, useEffect } from "react";
import ManagementSidebar from "./ManagementSidebar";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Tabs,
  Tab,
  Box,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import axios from "axios";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ActionButton from "../../Common/ActionButton";

// ── Design tokens ────────────────────────────────────────────────────────────
const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const contentHero = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 2.5, md: 3 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = { display: "flex", alignItems: "center", gap: 2 };

const heroIconBadge = {
  width: 52,
  height: 52,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.2rem", md: "1.5rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
};

const heroSubtitle = {
  fontSize: "0.85rem",
  color: "rgba(255,255,255,0.88)",
  mt: 0.4,
};

const filterCard = {
  backgroundColor: "var(--body-color-less2)",
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.12)",
  p: { xs: 2, md: 2.5 },
  mb: 3,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const tabsWrap = {
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  px: { xs: 1, md: 2 },
  backgroundColor: "var(--body-color-less2)",
};

const subTabsWrap = {
  borderBottom: "1px solid rgba(125,125,125,0.10)",
  px: { xs: 1, md: 2 },
  backgroundColor: "var(--body-color-less)",
};

const tabItem = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "var(--text-color)",
  opacity: 0.65,
  "&.Mui-selected": { color: "var(--text-color)", opacity: 1 },
};

const subTabItem = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.78rem",
  letterSpacing: "0.04em",
  color: "var(--text-color)",
  opacity: 0.6,
  "&.Mui-selected": { color: "var(--text-color)", opacity: 1 },
};

const modernTableContainer = {
  borderRadius: "0",
  border: "none",
  boxShadow: "none",
  overflowX: "auto",
  backgroundColor: "var(--body-color-less)",
};

const modernTableHeadRow = { backgroundColor: "var(--body-color-less2)" };

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
  whiteSpace: "nowrap",
};

const modernFieldStyle = {
  "& .MuiInputBase-root": {
    borderRadius: "10px",
    backgroundColor: "var(--body-color)",
    color: "var(--text-color)",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(125,125,125,0.25)" },
  "& .MuiInputLabel-root": { color: "var(--text-color)", opacity: 0.7 },
  "& .MuiSelect-icon": { color: "var(--text-color)" },
};
// ─────────────────────────────────────────────────────────────────────────────

const OrderManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { btncolor, brokerMarkets } = useSelector((state) => state);
  const openIndex = location.state?.openIndex;
  const [selectedSubAccount, setSelectedSubAccount] = useState("");
  const [subAccountsData, setSubAccountsData] = useState([]);

  const tradep = 6;

  const themeColor = localStorage.getItem("theme");

  const theme = useTheme();
  const [value, setValue] = useState("spot");
  const [subTabValue, setSubTabValue] = useState(0);
  const [filterTradeHistStatus, setFilterTradeHistStatus] = useState("ALL");
  const [filterMyOfferStatus, setFilterMyOfferStatus] = useState("ALL");
  const [filterFuturesTradeHistStatus, setFilterFuturesTradeHistStatus] =
    useState(0);
  const [filterFuturesOffersStatus, setFilterFuturesOffersStatus] =
    useState("ALL");
  const [filterOptionsHistStatus, setFilterOptionsHistStatus] = useState(0);
  const [filterOptionsOpenOfferStatus, setFilterOptionsOpenOfferStatus] =
    useState("ALL");
  const [showMyTradeInfo, setShowMyTradeInfo] = useState(false);
  const [showMyOfferInfo, setShowMyOfferInfo] = useState(false);

  const [selectedUuid, setSelectedUuid] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState("");

  const [tradeHistoryData, setTradeHistoryData] = useState([]);
  const [spotTradeHistoryData, setSpotTradeHistoryData] = useState(false);
  const [spotOffersData, setSpotOffersData] = useState([]);
  const [spotOpenOffersData, setSpotOpenOffersData] = useState(false);
  const [futuresTradeHistoryData, setFuturesTradeHistoryData] = useState([]);
  const [futuresTradeHistoryNoData, setFuturesTradeHistoryNoData] =
    useState(false);
  const [futuresOffersData, setFuturesOffersData] = useState([]);
  const [futuresOpenOffersNoData, setFuturesOpenOffersNoData] = useState(false);
  const [optionsTradeHistoryData, setOptionsTradeHistoryData] = useState([]);
  const [optionTradeHistoryNoData, setOptionTradeHistoryNoData] =
    useState(false);
  const [optionsOffersData, setOptionsOffersData] = useState([]);

  const [tradehistbyIdBuy, setTradehistbyIdBuy] = useState([]);
  const [tradehistbyIdSell, setTradehistbyIdSell] = useState([]);
  const [isloading, setIsloading] = useState(false);

  const [tradeofferByIdBuy, setTradeofferByIdBuy] = useState([]);
  const [tradeofferByIdSell, setTradeofferByIdSell] = useState([]);

  const [futureTradeHistByIdBuy, setFutureTradeHistByIdBuy] = useState([]);
  const [futureTradeHistByIdSell, setFutureTradeHistByIdSell] = useState([]);

  const [futureOpenOffersbyIdBuy, setFutureOpenOffersbyIdBuy] = useState([]);
  const [futureOpenOffersbyIdSell, setFutureOpenOffersbyIdSell] = useState([]);

  const [optionsHistbyIdBuy, setOptionsHistbyIdBuy] = useState([]);
  const [optionsHistbyIdSell, setOptionsHistbyIdSell] = useState([]);

  const [optionsOpenOffersbyIdBuy, setOptionsOpenOffersbyIdBuy] = useState([]);
  const [optionsOpenOffersbyIdSell, setOptionsOpenOffersbyIdSell] = useState([]);

  const getListOfAllSubAccounts = async () => {
    const payload = {
      parentId: localStorage.getItem("uuid"),
    };

    try {
      const response = await axios.post(
        `${WEBSERVICE}assetManagement/getAllSubAccounts`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      const res = response.data;

      if (res.statusCode !== 0) {
        showToast("error", res.message);
      } else {
        setSubAccountsData(res["sub accounts"]);
      }
    } catch (error) {
      showToast("error", "Something went wrong!");
    }
  };

  useEffect(() => {
    getListOfAllSubAccounts();
  }, []);

  const [selectedBaseCurrencyPrecision, setSelectedBaseCurrencyPrecision] =
    useState(0);

  useEffect(() => {
    const sellingAsset = localStorage.getItem("selling_crypto_asset");
    if (sellingAsset) {
      const precision = getSpecificCurrencyPrecision(
        sellingAsset.toUpperCase()
      );
      setSelectedBaseCurrencyPrecision(precision);
    }
  }, []);

  useEffect(() => {
    if (selectedEmail) {
      subAccountAllTrades();
      subAccountSpotOffers();
      subAccountFuturesAllTrades();
      subAccountFuturesOffers();
      subAccountOptionsAllTrades();
      subAccountOptionsOffers();
    }
  }, [selectedEmail]);

  const handleSubTabChange = (event, newValue) => {
    setSubTabValue(newValue);
  };

  const handleInfoSection = (section) => {
    if (section === "myTradeInfo") {
      setShowMyTradeInfo(!showMyTradeInfo);
    } else if (section === "myOfferInfo") {
      setShowMyOfferInfo(!showMyOfferInfo);
    }
  };

  const filterSpotOffer = (text_type) => {
    setFilterTradeHistStatus(text_type);

    if (text_type === "ALL") {
      setTradehistbyIdBuy([]);
      setTradehistbyIdSell([]);
    } else if (text_type === 1) {
      const buyData = tradeHistoryData.filter((item) => item.txn_type === 1);
      setTradehistbyIdBuy(buyData);
      setTradehistbyIdSell([]);
    } else {
      const sellData = tradeHistoryData.filter((item) => item.txn_type === 2);
      setTradehistbyIdSell(sellData);
      setTradehistbyIdBuy([]);
    }
  };

  const filterMyOffer = (text_type) => {
    setFilterMyOfferStatus(text_type);

    if (text_type === "ALL") {
      setTradeofferByIdBuy([]);
      setTradeofferByIdSell([]);
    } else if (text_type === 1) {
      const filteredBuy = spotOffersData.filter((item) => item.txn_type === 1);
      setTradeofferByIdBuy(filteredBuy);
      setTradeofferByIdSell([]);
      console.log("BUY", filteredBuy);
    } else if (text_type === 2) {
      const filteredSell = spotOffersData.filter((item) => item.txn_type === 2);
      setTradeofferByIdSell(filteredSell);
      setTradeofferByIdBuy([]);
      console.log("SELL", filteredSell);
    }
  };

  const myTradeTotal = (amount, price) => {
    return (Number(amount) * Number(price)).toFixed(2);
  };

  const selectedTabForOffers = (type) => {
    setValue(type);
  };

  const onEmailSelected = (value) => {
    const { email } = value;
    const id = value.uuid;
    setSelectedUuid(id);
    setSelectedEmail(email);
  };

  const logout = () => {
    localStorage.clear();
    navigate("/");
    window.location.reload();
  };

  const subAccountAllTrades = async () => {
    const payload = {
      parentId: localStorage.getItem("uuid"),
      uuid: selectedUuid,
    };

    try {
      const response = await axios.post(
        `${WEBSERVICE}assetManagement/subAccountAllSpotTrades`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      const res = response.data;
      setTradeHistoryData(res.tradeListResult);

      if (!res.tradeListResult || res.tradeListResult.length === 0) {
        setSpotTradeHistoryData(true);
      } else {
        setSpotTradeHistoryData(false);
      }
    } catch (error) {
      const reason = error.response?.data;

      if (reason?.error === "Token has been expired") {
        //logout
      } else {
        //logout
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      }
    }
  };

  const subAccountSpotOffers = async () => {
    const payload = {
      parentId: localStorage.getItem("uuid"),
      uuid: selectedUuid,
    };

    try {
      const response = await axios.post(
        `${WEBSERVICE}assetManagement/subAccountAllSpotOffers`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      const res = response.data;
      setSpotOffersData(res.tradeListResult);
      setSpotOpenOffersData(res.tradeListResult.length === 0);
    } catch (error) {
      if (error.response?.data?.error === "Token has been expired") {
        //logout
      } else {
        //logout
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      }
    }
  };

  const subAccountFuturesAllTrades = async () => {
    const payload = {
      parentId: localStorage.getItem("uuid"),
      uuid: selectedUuid,
    };

    try {
      const response = await axios.post(
        `${WEBSERVICE}assetManagement/subAccountAllFuturesTrades`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      const res = response.data;
      const tradeList = res.tradeListResult || [];

      setFuturesTradeHistoryData(tradeList);
      setFuturesTradeHistoryNoData(tradeList.length === 0);
    } catch (error) {
      const errorMessage = error?.response?.data?.error;

      if (errorMessage === "Token has been expired") {
        //logout
      } else {
        //logout
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      }
    }
  };

  const subAccountFuturesOffers = async () => {
    const payload = {
      parentId: localStorage.getItem("uuid"),
      uuid: selectedUuid,
    };

    try {
      const response = await axios.post(
        `${WEBSERVICE}assetManagement/subAccountAllFuturesOffers`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      const res = response.data;
      setFuturesOffersData(res.tradeListResult);

      if (res.tradeListResult.length === 0) {
        setFuturesOpenOffersNoData(true);
      } else {
        setFuturesOpenOffersNoData(false);
      }
    } catch (error) {
      const reason = error.response?.data;
      if (reason?.error === "invalid_token") {
        //logout
      } else {
        //logout
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      }
    }
  };

  const subAccountOptionsAllTrades = async () => {
    const payload = {
      parentId: localStorage.getItem("uuid"),
      uuid: selectedUuid,
    };

    try {
      const response = await axios.post(
        `${WEBSERVICE}assetManagement/subAccountAllOptionsTrades`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );
      const res = response.data;
      const tradeList = res.tradeListResult || [];

      setOptionsTradeHistoryData(tradeList);
      setOptionTradeHistoryNoData(tradeList.length === 0);
    } catch (error) {
      const errorMessage = error?.response?.data?.error;

      if (errorMessage === "Token has been expired") {
        //logout
      } else {
        //logout
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      }
    }
  };

  const subAccountOptionsOffers = async () => {
    const payload = {
      parentId: localStorage.getItem("uuid"),
      uuid: selectedUuid,
    };

    try {
      const response = await axios.post(
        `${WEBSERVICE}assetManagement/subAccountAllOptionsOffers`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      const res = response.data;
      setOptionsOffersData(res.tradeListResult);
    } catch (error) {
      const reason = error.response?.data?.error;
      if (reason === "Token has been expired") {
        //logout
      } else {
        //logout
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      }
    }
  };

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedSubAccount(selectedValue);
    onEmailSelected(selectedValue);
  };

  const filterFuturesTradeHist = (text_type) => {
    setFilterFuturesTradeHistStatus(text_type);

    if (text_type === "ALL") {
      setFutureTradeHistByIdBuy([]);
      setFutureTradeHistByIdSell([]);
    } else if (text_type === 1) {
      const buyData = futuresTradeHistoryData?.filter(
        (item) => item.txn_type === 1
      );
      setFutureTradeHistByIdBuy(buyData);
      setFutureTradeHistByIdSell([]);
      console.log("BUY", buyData);
    } else {
      const sellData = futuresTradeHistoryData?.filter(
        (item) => item.txn_type === 2
      );
      setFutureTradeHistByIdSell(sellData);
      setFutureTradeHistByIdBuy([]);
      console.log("SELL", sellData);
    }
  };

  const filterFuturesOpenOffers = (text_type) => {
    setFilterFuturesOffersStatus(text_type);

    if (text_type === "ALL") {
      // No need to filter, show everything in render
    } else if (text_type === 1) {
      const buyOffers = futuresOffersData?.filter(
        (item) => item.txn_type === 1
      );
      setFutureOpenOffersbyIdBuy(buyOffers);
      console.log("BUY", buyOffers);
    } else {
      const sellOffers = futuresTradeHistoryData?.filter(
        (item) => item.txn_type === 2
      );
      setFutureOpenOffersbyIdSell(sellOffers);
      console.log("SELL", sellOffers);
    }
  };

  const getSpecificCurrencyPrecision = (param) => {
    const list = JSON.parse(localStorage.getItem("precision_list_json")) || [];
    let precision = 0;
    for (let i = 0; i < list.length; i++) {
      if (
        list[i].CURRENCYID === param ||
        list[i].CURRENCY === param.toUpperCase()
      ) {
        precision = list[i].PRECISION;
        break;
      }
    }
    return precision;
  };

  const filterTradeOptionsHist = (text_type) => {
    setFilterOptionsHistStatus(text_type);

    if (text_type === "ALL") {
      // Show all transactions
    } else if (text_type === 1) {
      const buyData = optionsTradeHistoryData?.filter(
        (txn) => txn.txn_type === 1
      );
      setOptionsHistbyIdBuy(buyData);
      console.log("BUY", buyData);
    } else if (text_type === 2) {
      const sellData = optionsTradeHistoryData?.filter(
        (txn) => txn.txn_type === 2
      );
      setOptionsHistbyIdSell(sellData);
      console.log("SELL", sellData);
    }
  };

  const filterOptionsOpenOffer = (text_type) => {
    setFilterOptionsOpenOfferStatus(text_type);

    if (text_type === "ALL") {
      console.log("ALL", optionsOffersData);
    } else if (text_type === 1) {
      const buyData = optionsOffersData?.filter((item) => item.txn_type === 1);
      setOptionsOpenOffersbyIdBuy(buyData);
      console.log("BUY", buyData);
    } else if (text_type === 2) {
      const sellData = optionsOffersData?.filter((item) => item.txn_type === 2);
      setOptionsOpenOffersbyIdSell(sellData);
      console.log("SELL", sellData);
    }
  };

  const renderRow = (i) => (
    <TableRow key={`${i.transactionTimeStamp}-${i.amount}`} sx={modernTableBodyRow}>
      <TableCell sx={modernTableBodyCell}>{i.transactionTimeStamp}</TableCell>
      <TableCell sx={modernTableBodyCell}>
        {i.buying_asset_code} {i.selling_asset_code}
      </TableCell>
      <TableCell sx={modernTableBodyCell}>{i.amount}</TableCell>
      <TableCell sx={modernTableBodyCell}>{i.price}</TableCell>
      <TableCell sx={modernTableBodyCell}>{myTradeTotal(i.amount, i.price)}</TableCell>
      <TableCell sx={modernTableBodyCell}>
        <Typography color={i.txn_type === 1 ? "green" : "red"} sx={{ fontSize: "0.85rem" }}>
          {i.txn_type === 1 ? "BUY" : "SELL"}
        </Typography>
      </TableCell>
    </TableRow>
  );

  const renderNoDataRow = () => (
    <TableRow>
      <TableCell colSpan={6} sx={{ ...modernTableBodyCell, textAlign: "center", py: 4, opacity: 0.55 }}>
        No Data
      </TableCell>
    </TableRow>
  );

  const renderLoadingRow = () => (
    <TableRow>
      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
      </TableCell>
    </TableRow>
  );

  const renderRows = (data) =>
    data.map((m, i) => (
      <TableRow key={i} sx={modernTableBodyRow}>
        <TableCell sx={modernTableBodyCell}>{m.transactionTimeStamp}</TableCell>
        <TableCell sx={modernTableBodyCell}>{m.offer_id}</TableCell>
        <TableCell sx={modernTableBodyCell}>
          {m.txn_type === 1
            ? `${m.buying_asset_code}${m.selling_asset_code}`
            : `${m.selling_asset_code}${m.buying_asset_code}`}
        </TableCell>
        <TableCell sx={modernTableBodyCell}>{m.amount}</TableCell>
        <TableCell sx={modernTableBodyCell}>{m.price}</TableCell>
        <TableCell sx={modernTableBodyCell}>{(m.price * m.amount).toFixed(tradep)}</TableCell>
        <TableCell sx={modernTableBodyCell}>
          <Typography color={m.txn_type === 1 ? "success.main" : "error.main"} sx={{ fontSize: "0.85rem" }}>
            {m.txn_type === 1 ? "Buy" : "Sell"}
          </Typography>
        </TableCell>
      </TableRow>
    ));

  const renderRowF = (m, index) => (
    <TableRow key={index} className={`filter ${m.action?.toLowerCase()}`} sx={modernTableBodyRow}>
      <TableCell sx={modernTableBodyCell}>{m.transactionTimeStamp}</TableCell>
      <TableCell sx={modernTableBodyCell}>
        {m.assetPair || m.asset_pair}
        <small
          style={{
            marginLeft: 5,
            backgroundColor: "#616F8D",
            color: "#fff",
            padding: "3px",
            borderRadius: "5px",
          }}
        >
          {m.leverage}X
        </small>
      </TableCell>
      <TableCell sx={modernTableBodyCell}>{m.offerType}</TableCell>
      <TableCell
        sx={modernTableBodyCell}
        className={m.action === "buy" ? "text-green" : "text-danger"}
      >
        {m.amount}{" "}
        {m.action === "buy" ? m.buying_asset_code : m.selling_asset_code}
      </TableCell>
      <TableCell sx={modernTableBodyCell}>{Number(m.price).toFixed(2)}</TableCell>
      <TableCell sx={modernTableBodyCell}></TableCell>
      <TableCell sx={modernTableBodyCell}></TableCell>
      <TableCell sx={modernTableBodyCell}></TableCell>
    </TableRow>
  );

  const getTableData = () => {
    if (filterFuturesTradeHistStatus === 1) return futureTradeHistByIdBuy;
    if (filterFuturesTradeHistStatus === 2) return futureTradeHistByIdSell;
    return futuresTradeHistoryData;
  };

  const tableData = getTableData();
  const showData =
    tableData !== null &&
    filterFuturesTradeHistStatus !== 1 &&
    filterFuturesTradeHistStatus !== 2;

  const renderTableRows = (data) => {
    return data?.map((m, index) => (
      <TableRow key={index} sx={modernTableBodyRow}>
        <TableCell sx={modernTableBodyCell}>
          {new Date(m.transactionTimeStamp).toLocaleString()}
        </TableCell>
        <TableCell sx={modernTableBodyCell}>{m.offer_id}</TableCell>
        <TableCell sx={modernTableBodyCell}>{m.amount}</TableCell>
        <TableCell sx={modernTableBodyCell}>{m.price}</TableCell>
        <TableCell sx={modernTableBodyCell}>
          {(m.amount * m.price).toFixed(selectedBaseCurrencyPrecision)}
        </TableCell>
        <TableCell sx={modernTableBodyCell}>{m.offerType}</TableCell>
        <TableCell sx={modernTableBodyCell}>{m.leverage}X</TableCell>
        <TableCell sx={modernTableBodyCell}>
          <Typography color={m.txn_type === 1 ? "green" : "red"} sx={{ fontSize: "0.85rem" }}>
            {m.txn_type === 1 ? "Buy" : "Sell"}
          </Typography>
        </TableCell>
      </TableRow>
    ));
  };

  const shouldShowMain =
    futuresOffersData != null &&
    filterFuturesOffersStatus !== 1 &&
    filterFuturesOffersStatus !== 2;

  const renderRowsOption = (data) => {
    return data?.map((m, index) => (
      <TableRow key={index} className={`filter ${m.action.toLowerCase()}`} sx={modernTableBodyRow}>
        <TableCell sx={modernTableBodyCell}>{m.transactionTimeStamp}</TableCell>
        <TableCell sx={modernTableBodyCell}>
          {m.assetPair}
          <small
            style={{
              marginLeft: "5px",
              backgroundColor: "#616F8D",
              color: "#fff",
              padding: "3px",
              borderRadius: "5px",
            }}
          >
            {m.leverage}X
          </small>
        </TableCell>
        <TableCell sx={modernTableBodyCell}>{m.offerType}</TableCell>
        {m.action === "buy" ? (
          <TableCell className="text-green" sx={modernTableBodyCell}>
            {m.amount} {m.buying_asset_code}
          </TableCell>
        ) : (
          <TableCell className="text-danger" sx={modernTableBodyCell}>
            {m.amount} {m.selling_asset_code}
          </TableCell>
        )}
        <TableCell sx={modernTableBodyCell}>{Number(m.price).toFixed(2)}</TableCell>
        <TableCell sx={modernTableBodyCell}></TableCell>
        <TableCell sx={modernTableBodyCell}></TableCell>
        <TableCell sx={modernTableBodyCell}>{m.pnl}</TableCell>
      </TableRow>
    ));
  };

  const renderRowsOptionO = (dataList) => {
    return dataList.map((data, index) => (
      <TableRow key={index} sx={modernTableBodyRow}>
        <TableCell sx={modernTableBodyCell}>{data.transactionTimeStamp}</TableCell>
        <TableCell sx={modernTableBodyCell}>
          {data.assetPair}
          <small
            style={{
              marginLeft: 5,
              backgroundColor: "#616F8D",
              color: "#fff",
              padding: "3px",
              borderRadius: "5px",
            }}
          >
            {data.leverage}X
          </small>
        </TableCell>
        <TableCell sx={modernTableBodyCell}>{data.offerType}</TableCell>
        <TableCell
          sx={{ ...modernTableBodyCell, color: data.action === "buy" ? "green" : "red" }}
        >
          {data.amount}
          {data.action === "buy" ? data.buying_asset_code : data.selling_asset_code}
        </TableCell>
        <TableCell sx={modernTableBodyCell}>{Number(data.price).toFixed(2)}</TableCell>
        <TableCell sx={modernTableBodyCell}></TableCell>
        <TableCell sx={modernTableBodyCell}></TableCell>
        <TableCell sx={modernTableBodyCell}></TableCell>
      </TableRow>
    ));
  };

  // Info tooltip popover shared style
  const infoPopover = {
    position: "absolute",
    top: "50%",
    left: { xs: "10%", md: "50%" },
    zIndex: 1000,
  };

  const infoPopoverPaper = {
    display: "block",
    top: 10,
    backgroundColor: "var(--body-color-less2)",
    border: "1px solid rgba(125,125,125,0.15)",
    p: 2,
    width: 300,
    borderRadius: "12px",
  };

  return (
    <Grid container spacing={1} sx={{ mx: 1 }}>
      <Grid size={{ xs: 12, md: 2 }}>
        <ManagementSidebar index={openIndex} />
      </Grid>
      <Grid size={{ xs: 12, md: 10 }}>
        <Box sx={pageWrapper}>
          {/* Hero Banner */}
          <Box
            sx={{
              ...contentHero,
              background: `linear-gradient(135deg, ${alpha(btncolor || "#1976d2", 0.95)} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
            }}
          >
            <Box sx={heroContent}>
              <Box sx={heroIconBadge}>
                <ListAltOutlinedIcon sx={{ color: "#fff", fontSize: 26 }} />
              </Box>
              <Box>
                <Typography sx={heroTitle}>Order Management</Typography>
                <Typography sx={heroSubtitle}>
                  View and manage trading orders across sub-accounts
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Filter Card */}
          <Box sx={filterCard}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                <Typography sx={{ color: "var(--text-color)", fontWeight: 600, fontSize: "0.9rem" }}>
                  Select Sub Account
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 8, md: 4 }}>
                <FormControl fullWidth sx={modernFieldStyle}>
                  <InputLabel>Select Sub Accounts</InputLabel>
                  <Select
                    value={selectedSubAccount}
                    label="Select Sub Accounts"
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>Select Sub Accounts</em>
                    </MenuItem>
                    {subAccountsData?.map((account) => (
                      <MenuItem key={account.uuid} value={account}>
                        {account.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Main Content — only shows once sub-account selected */}
          {selectedSubAccount && (
            <Box sx={mainCard}>
              {/* Top-level market tabs (Spot / Futures / Options) */}
              <Box sx={tabsWrap}>
                <Tabs
                  value={value}
                  aria-label="market tabs"
                  variant="fullWidth"
                  sx={{
                    "& .MuiTabs-indicator": {
                      backgroundColor: btncolor,
                      height: "2px",
                      borderRadius: "2px",
                    },
                  }}
                >
                  <Tab
                    value="spot"
                    label="Spot"
                    sx={tabItem}
                    onClick={() => selectedTabForOffers("spot")}
                  />
                  {brokerMarkets?.isFutures === 1 && (
                    <Tab
                      value="futures"
                      label="Futures"
                      sx={tabItem}
                      onClick={() => {
                        selectedTabForOffers("futures");
                        subAccountFuturesAllTrades();
                      }}
                    />
                  )}
                  {brokerMarkets?.isOptions === 1 && (
                    <Tab
                      value="options"
                      label="Options"
                      sx={tabItem}
                      onClick={() => {
                        selectedTabForOffers("options");
                        subAccountOptionsAllTrades();
                      }}
                    />
                  )}
                </Tabs>
              </Box>

              {/* ── SPOT ───────────────────────────────────── */}
              {value === "spot" && (
                <Box>
                  <Box sx={subTabsWrap}>
                    <Tabs
                      value={subTabValue}
                      onChange={handleSubTabChange}
                      aria-label="spot sub tabs"
                      sx={{ "& .MuiTabs-indicator": { display: "none" } }}
                    >
                      <Tab label="Trade History" sx={subTabItem} />
                      <Tab
                        label="Open Orders"
                        sx={subTabItem}
                        onClick={subAccountSpotOffers}
                      />
                    </Tabs>
                  </Box>

                  {/* Spot — Trade History */}
                  {subTabValue === 0 && (
                    <Box>
                      {showMyTradeInfo && (
                        <Box sx={infoPopover}>
                          <Paper sx={infoPopoverPaper}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--text-color)" }}>
                                My Trades
                              </Typography>
                              <IconButton size="small" onClick={() => handleInfoSection("myTradeInfo")}>
                                <CloseIcon fontSize="small" sx={{ color: "var(--text-color)" }} />
                              </IconButton>
                            </Box>
                            <Typography variant="body2" sx={{ color: "var(--text-color)", opacity: 0.75 }}>
                              Trade History shows all successful BUY and SELL trades
                            </Typography>
                          </Paper>
                        </Box>
                      )}

                      <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2 }}>
                        <ActionButton
                          options={[
                            { label: "ALL", value: "ALL" },
                            { label: "BUY", value: 1 },
                            { label: "SELL", value: 2 },
                          ]}
                          active={filterTradeHistStatus}
                          onChange={filterSpotOffer}
                          btnColor={btncolor}
                        />
                        <IconButton onClick={() => handleInfoSection("myTradeInfo")}>
                          <InfoIcon sx={{ fontSize: "16px", color: "var(--text-color)", opacity: 0.5 }} />
                        </IconButton>
                      </Box>

                      <Box sx={{ pt: 1, pb: 2, px: 2 }}>
                        <TableContainer component={Box} sx={modernTableContainer}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={modernTableHeadRow}>
                                <TableCell sx={modernTableHeadCell}>Timestamp</TableCell>
                                <TableCell sx={modernTableHeadCell}>Asset Pair</TableCell>
                                <TableCell sx={modernTableHeadCell}>Quantity</TableCell>
                                <TableCell sx={modernTableHeadCell}>Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Total Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Offer Type</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filterTradeHistStatus !== 1 && filterTradeHistStatus !== 2 && (
                                <>
                                  {isloading && renderLoadingRow()}
                                  {tradeHistoryData?.length > 0
                                    ? tradeHistoryData.map(renderRow)
                                    : spotTradeHistoryData && renderNoDataRow()}
                                </>
                              )}
                              {filterTradeHistStatus === 1 && (
                                <>
                                  {isloading && renderLoadingRow()}
                                  {tradehistbyIdBuy ? tradehistbyIdBuy.map(renderRow) : renderNoDataRow()}
                                </>
                              )}
                              {filterTradeHistStatus === 2 && (
                                <>
                                  {isloading && renderLoadingRow()}
                                  {tradehistbyIdSell ? tradehistbyIdSell.map(renderRow) : renderNoDataRow()}
                                </>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  )}

                  {/* Spot — Open Orders */}
                  {subTabValue === 1 && (
                    <Box>
                      {showMyOfferInfo && (
                        <Box sx={{ position: "absolute", top: "40%", left: { xs: "10%", md: "50%" }, zIndex: 100 }}>
                          <Paper sx={infoPopoverPaper}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--text-color)" }}>
                                My Offers
                              </Typography>
                              <IconButton size="small" onClick={() => handleInfoSection("myOfferInfo")}>
                                <CloseIcon fontSize="small" sx={{ color: "var(--text-color)" }} />
                              </IconButton>
                            </Box>
                            <Typography variant="body2" sx={{ color: "var(--text-color)", opacity: 0.75 }}>
                              Open Orders shows all BUY and SELL Offers waiting to be executed
                            </Typography>
                          </Paper>
                        </Box>
                      )}

                      <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2 }}>
                        <ActionButton
                          options={[
                            { label: "ALL", value: "ALL" },
                            { label: "BUY", value: 1 },
                            { label: "SELL", value: 2 },
                          ]}
                          active={filterMyOfferStatus}
                          onChange={filterMyOffer}
                          btnColor={btncolor}
                        />
                        <IconButton onClick={() => handleInfoSection("myOfferInfo")}>
                          <InfoIcon sx={{ fontSize: "16px", color: "var(--text-color)", opacity: 0.5 }} />
                        </IconButton>
                      </Box>

                      <Box sx={{ pt: 1, pb: 2, px: 2 }}>
                        <TableContainer component={Box} sx={modernTableContainer}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={modernTableHeadRow}>
                                <TableCell sx={modernTableHeadCell}>Timestamp</TableCell>
                                <TableCell sx={modernTableHeadCell}>Order ID</TableCell>
                                <TableCell sx={modernTableHeadCell}>Asset Pair</TableCell>
                                <TableCell sx={modernTableHeadCell}>Quantity</TableCell>
                                <TableCell sx={modernTableHeadCell}>Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Total Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filterMyOfferStatus !== 1 && filterMyOfferStatus !== 2 && spotOffersData != null
                                ? renderRows(spotOffersData)
                                : null}
                              {filterMyOfferStatus === 1 && tradeofferByIdBuy != null
                                ? renderRows(tradeofferByIdBuy)
                                : null}
                              {filterMyOfferStatus === 2 && tradeofferByIdSell != null
                                ? renderRows(tradeofferByIdSell)
                                : null}
                              {filterMyOfferStatus !== 1 && filterMyOfferStatus !== 2 && spotOffersData == null && (
                                <TableRow>
                                  <TableCell colSpan={8} sx={{ ...modernTableBodyCell, textAlign: "center", py: 4, opacity: 0.55 }}>
                                    No Data
                                  </TableCell>
                                </TableRow>
                              )}
                              {filterMyOfferStatus === 1 && tradeofferByIdBuy == null && (
                                <TableRow>
                                  <TableCell colSpan={8} sx={{ ...modernTableBodyCell, textAlign: "center", py: 4, opacity: 0.55 }}>
                                    No Data
                                  </TableCell>
                                </TableRow>
                              )}
                              {filterMyOfferStatus === 2 && tradeofferByIdSell == null && (
                                <TableRow>
                                  <TableCell colSpan={8} sx={{ ...modernTableBodyCell, textAlign: "center", py: 4, opacity: 0.55 }}>
                                    No Data
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* ── FUTURES ─────────────────────────────────── */}
              {value === "futures" && (
                <Box>
                  <Box sx={subTabsWrap}>
                    <Tabs
                      value={subTabValue}
                      onChange={handleSubTabChange}
                      aria-label="futures sub tabs"
                      sx={{ "& .MuiTabs-indicator": { display: "none" } }}
                    >
                      <Tab
                        label="Trade History"
                        sx={subTabItem}
                        onClick={subAccountFuturesAllTrades}
                      />
                      <Tab
                        label="Open Orders"
                        sx={subTabItem}
                        onClick={subAccountFuturesOffers}
                      />
                    </Tabs>
                  </Box>

                  {/* Futures — Trade History */}
                  {subTabValue === 0 && (
                    <Box>
                      {showMyTradeInfo && (
                        <Box sx={{ position: "absolute", top: 70, left: { xs: 20, md: 168 }, zIndex: 1000 }}>
                          <Paper sx={infoPopoverPaper}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--text-color)" }}>
                                My Trades
                              </Typography>
                              <IconButton size="small" onClick={() => handleInfoSection("myTradeInfo")}>
                                <CloseIcon fontSize="small" sx={{ color: "var(--text-color)" }} />
                              </IconButton>
                            </Box>
                            <Typography variant="body2" sx={{ color: "var(--text-color)", opacity: 0.75 }}>
                              Trade History shows all successful BUY and SELL trades
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2 }}>
                        <ActionButton
                          options={[
                            { label: "ALL", value: "ALL" },
                            { label: "BUY", value: 1 },
                            { label: "SELL", value: 2 },
                          ]}
                          active={filterFuturesTradeHistStatus}
                          onChange={filterFuturesTradeHist}
                          btnColor={btncolor}
                        />
                        <IconButton onClick={() => handleInfoSection("myTradeInfo")}>
                          <InfoIcon sx={{ fontSize: "16px", color: "var(--text-color)", opacity: 0.5 }} />
                        </IconButton>
                      </Box>

                      <Box sx={{ pt: 1, pb: 2, px: 2 }}>
                        <TableContainer component={Box} sx={modernTableContainer}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={modernTableHeadRow}>
                                <TableCell sx={modernTableHeadCell}>Time</TableCell>
                                <TableCell sx={modernTableHeadCell}>Contract</TableCell>
                                <TableCell sx={modernTableHeadCell}>Offer Type</TableCell>
                                <TableCell sx={modernTableHeadCell}>Amount</TableCell>
                                <TableCell sx={modernTableHeadCell}>Entry Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Mark Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Liq. Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>PNL $(%)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody className="myoffer-block">
                              {isloading && renderLoadingRow()}
                              {!isloading && showData && tableData.map((m, idx) => renderRowF(m, idx))}
                            </TableBody>
                            {filterFuturesTradeHistStatus === 1 && (
                              <TableBody className="myoffer-block">
                                {isloading
                                  ? renderLoadingRow()
                                  : futureTradeHistByIdBuy?.length
                                  ? futureTradeHistByIdBuy.map((m, idx) => renderRowF(m, idx))
                                  : renderNoDataRow()}
                              </TableBody>
                            )}
                            {filterFuturesTradeHistStatus === 2 && (
                              <TableBody className="myoffer-block">
                                {isloading
                                  ? renderLoadingRow()
                                  : futureTradeHistByIdSell?.length
                                  ? futureTradeHistByIdSell.map((m, idx) => renderRowF(m, idx))
                                  : renderNoDataRow()}
                              </TableBody>
                            )}
                            {futureTradeHistByIdBuy === null && futuresTradeHistoryData === 1 && (
                              <TableBody className="myoffer-block">{renderNoDataRow()}</TableBody>
                            )}
                            {futureTradeHistByIdSell === null && futuresTradeHistoryData === 2 && (
                              <TableBody className="myoffer-block">{renderNoDataRow()}</TableBody>
                            )}
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  )}

                  {/* Futures — Open Orders */}
                  {subTabValue === 1 && (
                    <Box>
                      {showMyOfferInfo && (
                        <Box sx={{ position: "absolute", top: "40%", left: "50%" }}>
                          <Paper sx={infoPopoverPaper}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--text-color)" }}>
                                My Offers
                              </Typography>
                              <IconButton size="small" onClick={() => handleInfoSection("myOfferInfo")}>
                                <CloseIcon fontSize="small" sx={{ color: "var(--text-color)" }} />
                              </IconButton>
                            </Box>
                            <Typography variant="body2" sx={{ color: "var(--text-color)", opacity: 0.75 }}>
                              Open Orders shows all BUY and SELL Offers waiting to be executed
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2 }}>
                        <ActionButton
                          options={[
                            { label: "ALL", value: "ALL" },
                            { label: "BUY", value: 1 },
                            { label: "SELL", value: 2 },
                          ]}
                          active={filterFuturesOffersStatus}
                          onChange={filterFuturesOpenOffers}
                          btnColor={btncolor}
                        />
                        <IconButton onClick={() => handleInfoSection("myOfferInfo")}>
                          <InfoIcon sx={{ fontSize: "16px", color: "var(--text-color)", opacity: 0.5 }} />
                        </IconButton>
                      </Box>

                      <Box sx={{ pt: 1, pb: 2, px: 2 }}>
                        <TableContainer component={Box} sx={modernTableContainer}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={modernTableHeadRow}>
                                <TableCell sx={modernTableHeadCell}>Time</TableCell>
                                <TableCell sx={modernTableHeadCell}>Offer Id</TableCell>
                                <TableCell sx={modernTableHeadCell}>Amount</TableCell>
                                <TableCell sx={modernTableHeadCell}>Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Total Value</TableCell>
                                <TableCell sx={modernTableHeadCell}>Offer Type</TableCell>
                                <TableCell sx={modernTableHeadCell}>Lev</TableCell>
                                <TableCell sx={modernTableHeadCell}>Buy-Sell</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody className="myoffer-block">
                              {shouldShowMain && renderTableRows(futuresOffersData)}
                              {filterFuturesOffersStatus === 1 && futureOpenOffersbyIdBuy && renderTableRows(futureOpenOffersbyIdBuy)}
                              {filterFuturesOffersStatus === 2 && futureOpenOffersbyIdSell && renderTableRows(futureOpenOffersbyIdSell)}
                              {filterFuturesOffersStatus === 1 && !futureOpenOffersbyIdBuy && renderNoDataRow()}
                              {filterFuturesOffersStatus === 2 && !futureOpenOffersbyIdBuy && renderNoDataRow()}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* ── OPTIONS ─────────────────────────────────── */}
              {value === "options" && (
                <Box>
                  <Box sx={subTabsWrap}>
                    <Tabs
                      value={subTabValue}
                      onChange={handleSubTabChange}
                      aria-label="options sub tabs"
                      sx={{ "& .MuiTabs-indicator": { display: "none" } }}
                    >
                      <Tab
                        label="Trade History"
                        sx={subTabItem}
                        onClick={subAccountOptionsAllTrades}
                      />
                      <Tab
                        label="Open Orders"
                        sx={subTabItem}
                        onClick={subAccountOptionsOffers}
                      />
                    </Tabs>
                  </Box>

                  {/* Options — Trade History */}
                  {subTabValue === 0 && (
                    <Box>
                      {showMyTradeInfo && (
                        <Box sx={{ position: "absolute", top: 70, left: { xs: 20, md: 168 } }}>
                          <Paper sx={infoPopoverPaper}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--text-color)" }}>
                                My Trades
                              </Typography>
                              <IconButton size="small" onClick={() => handleInfoSection("myTradeInfo")}>
                                <CloseIcon fontSize="small" sx={{ color: "var(--text-color)" }} />
                              </IconButton>
                            </Box>
                            <Typography variant="body2" sx={{ color: "var(--text-color)", opacity: 0.75 }}>
                              Trade History shows all successful BUY and SELL trades
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2 }}>
                        <ActionButton
                          options={[
                            { label: "ALL", value: "ALL" },
                            { label: "BUY", value: 1 },
                            { label: "SELL", value: 2 },
                          ]}
                          active={filterOptionsHistStatus}
                          onChange={filterTradeOptionsHist}
                          btnColor={btncolor}
                        />
                        <IconButton onClick={() => handleInfoSection("myTradeInfo")}>
                          <InfoIcon sx={{ fontSize: "16px", color: "var(--text-color)", opacity: 0.5 }} />
                        </IconButton>
                      </Box>

                      <Box sx={{ pt: 1, pb: 2, px: 2 }}>
                        <TableContainer component={Box} sx={modernTableContainer}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={modernTableHeadRow}>
                                <TableCell sx={modernTableHeadCell}>Time</TableCell>
                                <TableCell sx={modernTableHeadCell}>Contract</TableCell>
                                <TableCell sx={modernTableHeadCell}>Offer Type</TableCell>
                                <TableCell sx={modernTableHeadCell}>Amount</TableCell>
                                <TableCell sx={modernTableHeadCell}>Entry Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Mark Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Liq. Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>PNL $(%)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filterOptionsHistStatus !== 1 && filterOptionsHistStatus !== 2 && optionsTradeHistoryData && renderRowsOption(optionsTradeHistoryData)}
                              {filterOptionsHistStatus === 1 && optionsHistbyIdBuy && renderRowsOption(optionsHistbyIdBuy)}
                              {filterOptionsHistStatus === 2 && optionsHistbyIdSell && renderRowsOption(optionsHistbyIdSell)}
                              {filterOptionsHistStatus === 1 && !optionsHistbyIdBuy && (
                                <TableRow>
                                  <TableCell colSpan={8} sx={{ ...modernTableBodyCell, textAlign: "center", py: 4, color: "error.main" }}>
                                    No Data
                                  </TableCell>
                                </TableRow>
                              )}
                              {filterOptionsHistStatus === 2 && !optionsHistbyIdSell && (
                                <TableRow>
                                  <TableCell colSpan={8} sx={{ ...modernTableBodyCell, textAlign: "center", py: 4, color: "error.main" }}>
                                    No Data
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  )}

                  {/* Options — Open Orders */}
                  {subTabValue === 1 && (
                    <Box>
                      {showMyOfferInfo && (
                        <Box sx={{ position: "absolute", top: "40%", left: "50%" }}>
                          <Paper sx={infoPopoverPaper}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--text-color)" }}>
                                My Offers
                              </Typography>
                              <IconButton size="small" onClick={() => handleInfoSection("myOfferInfo")}>
                                <CloseIcon fontSize="small" sx={{ color: "var(--text-color)" }} />
                              </IconButton>
                            </Box>
                            <Typography variant="body2" sx={{ color: "var(--text-color)", opacity: 0.75 }}>
                              Open Orders shows all BUY and SELL Offers waiting to be executed
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2 }}>
                        <ActionButton
                          options={[
                            { label: "ALL", value: "ALL" },
                            { label: "BUY", value: 1 },
                            { label: "SELL", value: 2 },
                          ]}
                          active={filterOptionsOpenOfferStatus}
                          onChange={filterOptionsOpenOffer}
                          btnColor={btncolor}
                        />
                        <IconButton onClick={() => handleInfoSection("myOfferInfo")}>
                          <InfoIcon sx={{ fontSize: "16px", color: "var(--text-color)", opacity: 0.5 }} />
                        </IconButton>
                      </Box>

                      <Box sx={{ pt: 1, pb: 2, px: 2 }}>
                        <TableContainer component={Box} sx={modernTableContainer}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={modernTableHeadRow}>
                                <TableCell sx={modernTableHeadCell}>Time</TableCell>
                                <TableCell sx={modernTableHeadCell}>Contract</TableCell>
                                <TableCell sx={modernTableHeadCell}>Offer Type</TableCell>
                                <TableCell sx={modernTableHeadCell}>Amount</TableCell>
                                <TableCell sx={modernTableHeadCell}>Entry Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Mark Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>Liq. Price</TableCell>
                                <TableCell sx={modernTableHeadCell}>PNL $(%)</TableCell>
                              </TableRow>
                            </TableHead>
                            {optionsOffersData != null && filterOptionsOpenOfferStatus !== 1 && filterOptionsOpenOfferStatus !== 2 && (
                              <TableBody>{renderRowsOptionO(optionsOffersData)}</TableBody>
                            )}
                            {filterOptionsOpenOfferStatus === 1 && (
                              optionsOpenOffersbyIdBuy ? (
                                <TableBody>{renderRowsOptionO(optionsOpenOffersbyIdBuy)}</TableBody>
                              ) : (
                                <TableBody>
                                  <TableRow>
                                    <TableCell colSpan={8} sx={{ ...modernTableBodyCell, textAlign: "center", py: 4, opacity: 0.55 }}>
                                      No Data
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              )
                            )}
                            {filterOptionsOpenOfferStatus === 2 && (
                              optionsOffersData ? (
                                <TableBody>{renderRowsOptionO(optionsOffersData)}</TableBody>
                              ) : (
                                <TableBody>
                                  <TableRow>
                                    <TableCell colSpan={8} sx={{ ...modernTableBodyCell, textAlign: "center", py: 4, opacity: 0.55 }}>
                                      No Data
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              )
                            )}
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default OrderManagement;
